import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { hinweis } from '../components/Hinweise';

const STATUS_LABELS = {
  entwurf:    { label: 'Entwurf',    cls: 'status-badge status-inaktiv' },
  aktiv:      { label: 'Aktiv',      cls: 'status-badge status-aktiv' },
  ausgebucht: { label: 'Ausgebucht', cls: 'status-badge status-warning' },
  abgesagt:   { label: 'Abgesagt',   cls: 'status-badge status-danger' },
  beendet:    { label: 'Beendet',    cls: 'status-badge status-inaktiv' },
};

function formatDatum(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function Angebote() {
  const navigate = useNavigate();
  const [angebote, setAngebote] = useState([]);
  const [typen, setTypen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', typ_id: '', search: '' });
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.typ_id) params.typ_id = filter.typ_id;
      if (filter.search) params.search = filter.search;
      const [ra, rt] = await Promise.all([
        api.get('/api/admin/angebote', { params }),
        api.get('/api/admin/angebot-typen'),
      ]);
      setAngebote(ra.data);
      setTypen(rt.data);
    } catch { setError('Angebote konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter.status, filter.typ_id]);

  const handleSearchKey = (e) => { if (e.key === 'Enter') load(); };

  const handleDelete = async (a) => {
    if (!window.confirm(`Angebot „${a.titel}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/admin/angebote/${a.id}`);
      await load();
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Fehler beim Löschen'); }
  };

  const handleStatusQuick = async (a, status) => {
    try {
      await api.put(`/api/admin/angebote/${a.id}`, { status });
      await load();
    } catch { hinweis.fehler('Fehler'); }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Angebote</h2>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/angebote/neu')}>+ Neues Angebot</button>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
        Kurse, Fahrten, Seminare und Veranstaltungen für das Widget.
      </p>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <input className="form-control" style={{ maxWidth: 240 }}
          placeholder="Suchen…" value={filter.search}
          onChange={e => setFilter(p => ({ ...p, search: e.target.value }))}
          onKeyDown={handleSearchKey} />
        <select className="form-control" style={{ maxWidth: 160 }}
          value={filter.status} onChange={e => setFilter(p => ({ ...p, status: e.target.value }))}>
          <option value="">Alle Status</option>
          {Object.entries(STATUS_LABELS).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
        </select>
        <select className="form-control" style={{ maxWidth: 180 }}
          value={filter.typ_id} onChange={e => setFilter(p => ({ ...p, typ_id: e.target.value }))}>
          <option value="">Alle Typen</option>
          {typen.map(t => <option key={t.id} value={t.id}>{t.icon} {t.name}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={load}>Suchen</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
      ) : angebote.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📋</div>
          <p>Noch keine Angebote angelegt.</p>
          <button className="btn btn-primary btn-sm" style={{ marginTop: '0.75rem' }} onClick={() => navigate('/angebote/neu')}>
            Erstes Angebot erstellen
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {angebote.map(a => {
            const sb = STATUS_LABELS[a.status] || STATUS_LABELS.entwurf;
            const belegung = a.max_teilnehmer
              ? `${a.anmeldungen_count}/${a.max_teilnehmer}`
              : `${a.anmeldungen_count} Anm.`;
            const belegungPct = a.max_teilnehmer ? (a.anmeldungen_count / a.max_teilnehmer) * 100 : 0;

            return (
              <div key={a.id} className="card" style={{ padding: '1rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                {/* Bild/Icon */}
                <div style={{
                  width: 64, height: 64, borderRadius: 8, flexShrink: 0, overflow: 'hidden',
                  background: a.typ_farbe || '#0099cc',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem',
                }}>
                  {a.bild_pfad
                    ? <img src={`/uploads/${a.bild_pfad}`} alt={a.titel} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : (a.typ_icon || '📌')}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
                    {a.typ_name && (
                      <span style={{
                        fontSize: '0.7rem', fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                        background: (a.typ_farbe || '#0099cc') + '22',
                        color: a.typ_farbe || '#0099cc', border: `1px solid ${a.typ_farbe || '#0099cc'}44`,
                      }}>{a.typ_icon} {a.typ_name}</span>
                    )}
                    <span className={sb.cls}>{sb.label}</span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--primary)', marginBottom: '0.15rem' }}>
                    {a.titel}
                  </div>
                  {a.untertitel && <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{a.untertitel}</div>}
                  <div style={{ display: 'flex', gap: '1rem', fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                    {a.naechster_termin && <span>📅 {formatDatum(a.naechster_termin)}</span>}
                    {a.ort && <span>📍 {a.ort}</span>}
                    <span title="Anmeldungen / Warteliste">
                      👥 {belegung}
                      {a.warteliste_count > 0 && <span style={{ color: '#e65100', marginLeft: 4 }}>+{a.warteliste_count} WL</span>}
                    </span>
                  </div>
                  {a.max_teilnehmer && (
                    <div style={{ marginTop: '0.4rem', height: 4, background: '#e0e0e0', borderRadius: 2, maxWidth: 200 }}>
                      <div style={{
                        height: '100%', borderRadius: 2,
                        background: belegungPct >= 100 ? '#c62828' : belegungPct >= 80 ? '#e65100' : '#2e7d32',
                        width: `${Math.min(belegungPct, 100)}%`,
                        transition: 'width 0.3s',
                      }} />
                    </div>
                  )}
                </div>

                {/* Aktionen */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => navigate(`/angebote/${a.id}`)}>Verwalten</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/angebote/${a.id}/bearbeiten`)}>Bearbeiten</button>
                  {a.status === 'entwurf' && (
                    <button className="btn btn-success btn-sm" onClick={() => handleStatusQuick(a, 'aktiv')}>Veröffentlichen</button>
                  )}
                  {a.status === 'aktiv' && (
                    <button className="btn btn-secondary btn-sm" onClick={() => handleStatusQuick(a, 'entwurf')}>Pausieren</button>
                  )}
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a)}>Löschen</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
